import React, { useEffect, useState } from 'react';
import { MIcon } from '../../lib/micon';
import { apiFetch, apiPut } from '../../lib/api';
import { toast } from '../../lib/toast';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdSheetHeader, LdField, LdSegmented, LdSheetSaveBar } from '../../components/ld/LdForm';

// Welle N2: Noten-Einstellungen je Lerngruppe (Lehrkraft × Klasse × Fach): Gewichtung
// schriftlich/mündlich und geplante Klassenarbeiten je Halbjahr. Rechnung und Regeltext
// kommen danach vom Server (GET/PUT /api/noten/einstellungen).
type Einstellung = {
  anteil_schriftlich: number; anteil_muendlich: number; ist_voreinstellung: boolean; voreinstellung: number;
  ka_geplant_hj1: number | null; ka_geplant_hj2: number | null; bereich: { min: number; max: number };
  regeltext: string; hinweis: string;
};
const KA_OPTIONEN: { value: number | null; label: string }[] = [{ value: null, label: '–' }, ...[1, 2, 3, 4].map(n => ({ value: n, label: String(n) }))];

export function NotenEinstellungenSheet({ open, klasseId, klasseName, fach, onClose, onGespeichert }: {
  open: boolean; klasseId: number; klasseName: string; fach: string; onClose: () => void; onGespeichert: () => void;
}) {
  const [e, setE] = useState<Einstellung | null>(null);
  const [anteil, setAnteil] = useState(50);
  const [ka1, setKa1] = useState<number | null>(null);
  const [ka2, setKa2] = useState<number | null>(null);
  const [laden, setLaden] = useState(false);
  const [fehler, setFehler] = useState('');

  useEffect(() => {
    if (!open) return;
    setE(null); setFehler('');
    apiFetch<Einstellung>(`/api/noten/einstellungen?klasse_id=${klasseId}&fach=${encodeURIComponent(fach)}`)
      .then(d => { setE(d); setAnteil(d.anteil_schriftlich); setKa1(d.ka_geplant_hj1); setKa2(d.ka_geplant_hj2); })
      .catch(err => setFehler(err?.message ?? 'Konnte nicht geladen werden.'));
  }, [open, klasseId, fach]);

  async function speichern() {
    setLaden(true); setFehler('');
    try {
      await apiPut('/api/noten/einstellungen', { klasse_id: klasseId, fach, anteil_schriftlich: anteil, ka_geplant_hj1: ka1, ka_geplant_hj2: ka2 });
      toast('Noten-Einstellungen gespeichert', 'success');
      onGespeichert();
    } catch (err: any) { setFehler(err?.message ?? 'Speichern fehlgeschlagen.'); }
    finally { setLaden(false); }
  }

  const min = e?.bereich.min ?? 30, max = e?.bereich.max ?? 70;
  return (
    <LdSheet open={open} onClose={onClose} maxWidth={480} ariaLabel="Noten-Einstellungen">
      <div style={{ padding: '8px 24px 24px', display: 'flex', flexDirection: 'column', gap: 16 }}>
        <LdSheetHeader icon={<MIcon name="tune" size={20} />} title="Noten-Einstellungen" subtitle={`${klasseName} · ${fach}`} onClose={onClose} />
        <div role="note" style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '10px 12px', borderRadius: 12, background: 'var(--ld-subtab-active-bg)', fontSize: 13, lineHeight: 1.45, color: 'var(--ld-text-primary)' }}>
          <MIcon name="gavel" size={17} style={{ color: '#6B5FD4', marginTop: 1 }} />
          <span>{e?.hinweis ?? 'Maßgeblich ist der Beschluss deiner Fachkonferenz.'}</span>
        </div>
        <LdField label="Gewichtung" helper={e ? (e.ist_voreinstellung ? `Voreinstellung für ${fach}: ${e.voreinstellung} % schriftlich` : `Erlaubt: ${min}–${max} % schriftlich`) : undefined} error={fehler || undefined}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 700, color: 'var(--ld-text-primary)' }}>
              <span>Schriftlich {anteil} %</span><span>Mündlich {100 - anteil} %</span>
            </div>
            <input type="range" min={min} max={max} step={5} value={anteil} disabled={!e}
              onChange={ev => setAnteil(Number(ev.target.value))} aria-label="Anteil schriftlich in Prozent"
              aria-valuetext={`Schriftlich ${anteil} %, mündlich ${100 - anteil} %`} style={{ width: '100%', accentColor: '#6B5FD4' }} />
          </div>
        </LdField>
        <LdField label="Geplante Klassenarbeiten im Halbjahr 1" helper="Fehlt ein Termin, zeigt der Noten-Tab „KA 2 · noch ohne Termin“.">
          <LdSegmented value={ka1} onChange={setKa1} options={KA_OPTIONEN} />
        </LdField>
        <LdField label="Geplante Klassenarbeiten im Halbjahr 2">
          <LdSegmented value={ka2} onChange={setKa2} options={KA_OPTIONEN} />
        </LdField>
        <LdSheetSaveBar onClick={speichern} disabled={!e} loading={laden}>Speichern</LdSheetSaveBar>
      </div>
    </LdSheet>
  );
}
